'use client'

/**
 * Switch the site between the light and the dark field.
 *
 * ── Why the state lives on `<html>` and not in React ────────────────────────
 *
 * The same argument the sidebar toggle makes, and more forcefully: nearly every
 * page here is a server component rendered fresh on navigation, so React state
 * holding the theme would have to survive a tree the server owns — and until it
 * caught up, the page would paint in the OTHER theme and flip. A full-screen
 * flash, on every load, in the one direction the eye cannot ignore.
 *
 * So the choice is an attribute on the document element, written by a
 * synchronous script before the first paint (`themeBootScript`), and the palette
 * follows from CSS. This button's only jobs are to flip that attribute and to
 * remember the flip. It holds no state that anything renders from — which is
 * also why it has no `useState`, and why both icons and both labels are in the
 * markup with CSS choosing between them (see `.theme-in-dark` in globals.css).
 *
 * ── Where it appears ───────────────────────────────────────────────────────
 *
 * The landing corner, the site header (so: results, entity, about and the whole
 * dashboard) and the console topbar. Not on the auth pages, which have no chrome
 * to put it in — the attribute still applies there, it just cannot be changed
 * from a sign-in form.
 */
import { useEffect } from 'react'
import { themeAttribute, themeStorageKey, type Theme } from './theme'

/** The theme in force right now: the explicit choice, or the system's answer. */
function activeTheme(): Theme {
  const forced = document.documentElement.getAttribute(themeAttribute)
  if (forced === 'light' || forced === 'dark') return forced
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Repoint the browser chrome at the theme actually in force.
 *
 * `viewport.themeColor` in the root layout declares two `<meta name="theme-color">`
 * elements keyed on `prefers-color-scheme`, and a browser picks between them by
 * the SYSTEM setting — which is exactly the thing this control overrules. Left
 * alone, a reader who switches to dark on a light phone gets a black page under
 * a white address bar, which is the banding that metadata exists to prevent.
 *
 * The colour is read back from the painted page rather than restated here, so
 * there is no second copy of --bg to keep in step. Dropping `media` is what makes
 * the override stick: both elements then match, and the first one wins.
 */
function syncThemeColor(): void {
  const painted = getComputedStyle(document.body).backgroundColor
  if (!painted) return
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.removeAttribute('media')
    meta.setAttribute('content', painted)
  })
}

export function ThemeToggle() {
  /*
   * Only for a reader who HAS chosen. With nothing stored the two media-keyed
   * metas are already right, and flattening them would freeze the browser chrome
   * at whatever the system said on this page load.
   *
   * It runs after paint, unlike the boot script, because nothing visible depends
   * on it — and because the metas are rendered by the framework, so reaching for
   * them from a script in `<head>` would be a race with the very markup that
   * creates them.
   */
  useEffect(() => {
    let stored: string | null = null
    try {
      stored = localStorage.getItem(themeStorageKey)
    } catch {
      /* storage disabled — nothing was restored, so nothing needs syncing */
    }
    if (stored === 'light' || stored === 'dark') syncThemeColor()
  }, [])

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={() => {
        const next: Theme = activeTheme() === 'dark' ? 'light' : 'dark'
        document.documentElement.setAttribute(themeAttribute, next)
        // A preference, not data. A browser with storage disabled gets a theme
        // that forgets between pages, which is a far better failure than a site
        // that will not render.
        try {
          localStorage.setItem(themeStorageKey, next)
        } catch {
          /* private mode, or storage full — the switch still works for this page */
        }
        syncThemeColor()
      }}
    >
      {/* The glyph names the theme you would MOVE TO, not the one you are in:
          the sun is offered on the black field, the moon on the white. Both are
          rendered; CSS shows the one that matches the current theme. */}
      <svg className="theme-in-dark" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <circle
          cx="10"
          cy="10"
          r="3.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M10 1.7v2.2M10 16.1v2.2M1.7 10h2.2M16.1 10h2.2M4.13 4.13l1.56 1.56M14.31 14.31l1.56 1.56M15.87 4.13l-1.56 1.56M5.69 14.31l-1.56 1.56"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
      <svg className="theme-in-light" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path
          d="M17.5 10.66A7.5 7.5 0 1 1 9.34 2.5a5.83 5.83 0 0 0 8.16 8.16z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* The accessible name has to say which way the button goes, and it changes
          with the theme — so, like the glyphs, both are here and CSS picks. */}
      <span className="sr-only theme-in-dark">Switch to the light theme</span>
      <span className="sr-only theme-in-light">Switch to the dark theme</span>
    </button>
  )
}
